import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCircle, BellRinging } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";

export function NotificationBell() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
    staleTime: 10000,
  });
  const unread = data?.unread || 0;
  const items = data?.notifications || [];

  const readOne = useMutation({
    mutationFn: async (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const readAll = useMutation({
    mutationFn: async () => api.post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const onClick = (n) => {
    if (!n.read) readOne.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notifications"
          className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors duration-200">
          {unread > 0 ? <BellRinging className="h-5 w-5 text-primary" weight="fill" /> : <Bell className="h-5 w-5" weight="bold" />}
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-bold text-sm">Notifications</p>
          {unread > 0 && (
            <button onClick={() => readAll.mutate()} data-testid="notification-read-all"
              className="text-xs text-primary hover:underline flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5" weight="bold" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="notification-empty">No notifications yet.</p>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => onClick(n)} data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/40 transition-colors duration-200 ${n.read ? "" : "bg-primary/5"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                  <div className={n.read ? "pl-4" : ""}>
                    <p className="text-sm font-semibold leading-tight">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
